import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../api/client';

/**
 * Platform Workflows — operator-side tenant lifecycle processes
 * (onboarding, support plans, renewals, offboarding). Each workflow is anchored
 * to a tenant and tracked as an ordered checklist. Backed by /platform/workflows.
 */

interface Category { id: string; name: string; icon: string; color: string }
interface Step {
  id: string;
  title: string;
  description: string;
  assignee: string;
  icon: string;
  status: string; // pending | active | completed
  sortOrder: number;
}
interface TenantRef { id: string; name: string; slug: string }
interface Workflow {
  id: string;
  name: string;
  categoryId: string;
  tenantId: string | null;
  icon: string;
  color: string;
  status: string; // draft | active | completed
  category?: Category;
  tenant?: TenantRef | null;
  steps: Step[];
}
interface TenantOption { id: string; name: string; slug: string }

function nextStatus(s: string): string {
  return s === 'draft' ? 'active' : s === 'active' ? 'completed' : 'draft';
}
function progressPct(w: Workflow): number {
  if (w.steps.length === 0) return 0;
  const done = w.steps.filter((s) => s.status === 'completed').length;
  return Math.round((done / w.steps.length) * 100);
}

export function PlatformWorkflowsPage() {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tenantFilter, setTenantFilter] = useState<string>('');
  const [selected, setSelected] = useState<Workflow | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  async function loadAll() {
    try {
      const [wfs, cats, tens] = await Promise.all([
        apiClient<Workflow[]>('/platform/workflows'),
        apiClient<Category[]>('/platform/workflow-categories'),
        apiClient<TenantOption[]>('/platform/tenants'),
      ]);
      setWorkflows(wfs);
      setCategories(cats);
      setTenants(tens);
      setError('');
    } catch (e: any) {
      setError(e?.message || 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  // Keep the selected workflow in sync after mutations.
  useEffect(() => {
    if (selected) {
      const fresh = workflows.find((w) => w.id === selected.id);
      if (fresh) setSelected(fresh);
    }
  }, [workflows]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(
    () => (tenantFilter ? workflows.filter((w) => w.tenantId === tenantFilter) : workflows),
    [workflows, tenantFilter],
  );

  // Group by tenant for display.
  const byTenant = useMemo(() => {
    const m = new Map<string, { tenant: TenantRef | null; items: Workflow[] }>();
    for (const w of filtered) {
      const key = w.tenantId || 'none';
      if (!m.has(key)) m.set(key, { tenant: w.tenant ?? null, items: [] });
      m.get(key)!.items.push(w);
    }
    return [...m.values()];
  }, [filtered]);

  async function refreshKeepingSelection() {
    const wfs = await apiClient<Workflow[]>('/platform/workflows');
    setWorkflows(wfs);
  }

  // ── Detail view ──────────────────────────────────────────────────────────
  if (selected) {
    return (
      <WorkflowDetail
        workflow={selected}
        categories={categories}
        onBack={() => setSelected(null)}
        onChanged={refreshKeepingSelection}
        onDeleted={() => { setSelected(null); refreshKeepingSelection(); }}
      />
    );
  }

  return (
    <div>
      <div style={styles.headingRow}>
        <div>
          <h1 style={styles.h1}>Workflows</h1>
          <p style={styles.sub}>Tenant lifecycle processes — onboarding, support plans, renewals.</p>
        </div>
        <button style={styles.primaryBtn} onClick={() => setShowCreate(true)}>
          + New workflow
        </button>
      </div>

      {error && <div style={styles.error} onClick={() => setError('')}>{error}</div>}

      <div style={styles.filterRow}>
        <label style={styles.filterLabel}>Tenant</label>
        <select style={styles.select} value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)}>
          <option value="">All tenants</option>
          {tenants.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div style={styles.muted}>Loading workflows…</div>
      ) : filtered.length === 0 ? (
        <div style={styles.emptyCard}>
          No workflows yet. Create one to track a tenant’s onboarding or support plan.
        </div>
      ) : (
        byTenant.map((group) => (
          <section key={group.tenant?.id || 'none'} style={styles.tenantGroup}>
            <div style={styles.tenantGroupHead}>
              <span style={styles.tenantGroupName}>{group.tenant?.name || 'Unassigned'}</span>
              {group.tenant && <span style={styles.tenantGroupSlug}>{group.tenant.slug}</span>}
            </div>
            <div style={styles.cardGrid}>
              {group.items.map((w) => {
                const pct = progressPct(w);
                return (
                  <button key={w.id} style={styles.wfCard} onClick={() => setSelected(w)}>
                    <div style={styles.wfTop}>
                      <span style={styles.wfIcon}>{w.icon}</span>
                      <span style={{ ...styles.statusPill, ...statusPillStyle(w.status) }}>{w.status}</span>
                    </div>
                    <div style={styles.wfName}>{w.name}</div>
                    <div style={styles.wfCat}>{w.category?.name}</div>
                    <div style={styles.progressTrack}>
                      <div style={{ ...styles.progressFill, width: `${pct}%` }} />
                    </div>
                    <div style={styles.wfMeta}>
                      {w.steps.filter((s) => s.status === 'completed').length}/{w.steps.length} steps · {pct}%
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        ))
      )}

      {showCreate && (
        <CreateWorkflowModal
          tenants={tenants}
          categories={categories}
          defaultTenantId={tenantFilter || undefined}
          onClose={() => setShowCreate(false)}
          onCreated={async (wf) => {
            setShowCreate(false);
            await refreshKeepingSelection();
            setSelected(wf);
          }}
        />
      )}
    </div>
  );
}

// ── Create modal ─────────────────────────────────────────────────────────
function CreateWorkflowModal({
  tenants,
  categories,
  defaultTenantId,
  onClose,
  onCreated,
}: {
  tenants: TenantOption[];
  categories: Category[];
  defaultTenantId?: string;
  onClose: () => void;
  onCreated: (wf: Workflow) => void;
}) {
  const [tenantId, setTenantId] = useState(defaultTenantId || '');
  const [categoryId, setCategoryId] = useState(categories[0]?.id || '');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      const wf = await apiClient<Workflow>('/platform/workflows', {
        method: 'POST',
        body: { tenantId, categoryId, name: name.trim() },
      });
      onCreated(wf);
    } catch (e: any) {
      setErr(e?.message || 'Failed to create workflow');
      setBusy(false);
    }
  }

  return (
    <div style={styles.scrim} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={styles.h2}>New workflow</h2>
        {err && <div style={styles.error}>{err}</div>}
        <form onSubmit={submit}>
          <label style={styles.label}>Tenant</label>
          <select style={styles.input} value={tenantId} onChange={(e) => setTenantId(e.target.value)} required>
            <option value="">Select a tenant…</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>

          <label style={styles.label}>Playbook</label>
          <select style={styles.input} value={categoryId} onChange={(e) => setCategoryId(e.target.value)} required>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
          </select>

          <label style={styles.label}>Name</label>
          <input style={styles.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Onboarding" required />

          <div style={styles.modalActions}>
            <button type="button" style={styles.secondaryBtn} onClick={onClose}>Cancel</button>
            <button type="submit" style={styles.primaryBtn} disabled={busy || !tenantId || !categoryId}>
              {busy ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Detail / step editor ───────────────────────────────────────────────────
function WorkflowDetail({
  workflow,
  categories,
  onBack,
  onChanged,
  onDeleted,
}: {
  workflow: Workflow;
  categories: Category[];
  onBack: () => void;
  onChanged: () => Promise<void>;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const pct = progressPct(workflow);
  const category = categories.find((c) => c.id === workflow.categoryId);
  // Preset assignees for operator workflows: the tenant, EJT (operator), + free-text "Other".
  const assigneeOptions = [workflow.tenant?.name || 'Tenant', 'EJT (operator)'];

  async function cycleStatus() {
    setBusy(true);
    try {
      await apiClient(`/platform/workflows/${workflow.id}`, { method: 'PATCH', body: { status: nextStatus(workflow.status) } });
      await onChanged();
    } finally {
      setBusy(false);
    }
  }
  async function addStep() {
    await apiClient(`/platform/workflows/${workflow.id}/steps`, { method: 'POST', body: { title: 'New step', description: '' } });
    await onChanged();
  }
  async function updateStep(stepId: string, patch: Record<string, string>) {
    await apiClient(`/platform/workflow-steps/${stepId}`, { method: 'PATCH', body: patch });
    await onChanged();
  }
  async function deleteStep(stepId: string) {
    if (workflow.steps.length <= 1) return;
    await apiClient(`/platform/workflow-steps/${stepId}`, { method: 'DELETE' });
    await onChanged();
  }
  async function deleteWorkflow() {
    if (!window.confirm(`Delete workflow "${workflow.name}"?`)) return;
    await apiClient(`/platform/workflows/${workflow.id}`, { method: 'DELETE' });
    onDeleted();
  }

  return (
    <div>
      <button style={styles.back} onClick={onBack}>← All workflows</button>

      <div style={styles.detailHead}>
        <div style={{ ...styles.detailIcon, borderColor: workflow.color, background: workflow.color + '18' }}>{workflow.icon}</div>
        <div style={{ flex: 1 }}>
          <h1 style={styles.h1}>{workflow.name}</h1>
          <div style={styles.detailMeta}>
            {workflow.tenant?.name || 'Unassigned'} · {category?.name} · {workflow.steps.length} steps · {pct}%
          </div>
        </div>
        <button style={{ ...styles.statusBtn, ...statusPillStyle(workflow.status) }} onClick={cycleStatus} disabled={busy} title="Advance status">
          {workflow.status} ↻
        </button>
        <button style={styles.deleteBtn} onClick={deleteWorkflow} title="Delete workflow">🗑️</button>
      </div>

      <div style={styles.progressTrackLg}>
        <div style={{ ...styles.progressFill, width: `${pct}%` }} />
      </div>

      <div style={styles.stepsHead}>
        <h2 style={styles.h2}>Steps</h2>
        <button style={styles.secondaryBtn} onClick={addStep}>+ Add step</button>
      </div>

      {workflow.steps.map((step, idx) => (
        <StepEditor
          key={step.id}
          step={step}
          index={idx}
          canDelete={workflow.steps.length > 1}
          assigneeOptions={assigneeOptions}
          onSave={(patch) => updateStep(step.id, patch)}
          onDelete={() => deleteStep(step.id)}
        />
      ))}
    </div>
  );
}

const OTHER = '__other__';

function StepEditor({
  step,
  index,
  canDelete,
  assigneeOptions,
  onSave,
  onDelete,
}: {
  step: Step;
  index: number;
  canDelete: boolean;
  assigneeOptions: string[];
  onSave: (patch: Record<string, string>) => void;
  onDelete: () => void;
}) {
  const [title, setTitle] = useState(step.title);
  const [description, setDescription] = useState(step.description);
  // Assignee is a preset dropdown (tenant / EJT) + "Other" free-text.
  const presetMatch = assigneeOptions.includes(step.assignee);
  const [assigneeChoice, setAssigneeChoice] = useState<string>(
    presetMatch ? step.assignee : step.assignee && step.assignee !== 'Unassigned' ? OTHER : '',
  );
  const [assigneeOther, setAssigneeOther] = useState<string>(presetMatch ? '' : step.assignee === 'Unassigned' ? '' : step.assignee);

  const resolvedAssignee =
    assigneeChoice === OTHER ? assigneeOther.trim() : assigneeChoice || 'Unassigned';
  const dirty =
    title !== step.title ||
    description !== step.description ||
    resolvedAssignee !== step.assignee;

  return (
    <div style={styles.stepCard}>
      <div style={styles.stepNum}>{index + 1}</div>
      <div style={{ flex: 1 }}>
        <input style={styles.stepTitle} value={title} onChange={(e) => setTitle(e.target.value)} />
        <textarea style={styles.stepDesc} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Describe this step…" />
        <div style={styles.stepRow}>
          <select
            style={styles.assigneeSelect}
            value={assigneeChoice}
            onChange={(e) => setAssigneeChoice(e.target.value)}
            aria-label={`Assignee for step ${index + 1}`}
          >
            <option value="">Unassigned</option>
            {assigneeOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
            <option value={OTHER}>Other…</option>
          </select>
          {assigneeChoice === OTHER && (
            <input
              style={styles.assigneeInput}
              value={assigneeOther}
              onChange={(e) => setAssigneeOther(e.target.value)}
              placeholder="Type a name"
              aria-label="Other assignee name"
            />
          )}
          <select
            style={styles.stepStatusSelect}
            value={step.status}
            onChange={(e) => onSave({ status: e.target.value })}
          >
            <option value="pending">Not started</option>
            <option value="active">In progress</option>
            <option value="completed">Done</option>
          </select>
          {dirty && (
            <button
              style={styles.saveBtn}
              onClick={() => onSave({ title, description, assignee: resolvedAssignee })}
            >
              Save
            </button>
          )}
          {canDelete && (
            <button style={styles.stepDeleteBtn} onClick={onDelete} title="Delete step" aria-label={`Delete step ${index + 1}`}>🗑️</button>
          )}
        </div>
        {step.assignee && step.assignee !== 'Unassigned' && (
          <div style={styles.assigneeCurrent}>Assigned to {step.assignee}</div>
        )}
      </div>
    </div>
  );
}

function statusPillStyle(status: string): React.CSSProperties {
  if (status === 'active') return { background: '#dbeafe', color: '#1e40af' };
  if (status === 'completed') return { background: '#dcfce7', color: '#166534' };
  return { background: '#f1f5f9', color: '#475569' };
}

const ACCENT = '#4f46e5';
const BORDER = '#e5e7eb';
const MUTED = '#6b7280';
const INK = '#0b1120';

const styles: Record<string, React.CSSProperties> = {
  headingRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 },
  h1: { fontSize: '1.6rem', fontWeight: 800, margin: 0, color: INK },
  sub: { color: MUTED, fontSize: '0.9rem', margin: '4px 0 0' },
  h2: { fontSize: '1.05rem', fontWeight: 700, margin: 0, color: INK },
  primaryBtn: { background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' },
  secondaryBtn: { background: '#eef2ff', color: ACCENT, border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' },
  filterRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 },
  filterLabel: { fontSize: '0.8rem', fontWeight: 700, color: MUTED },
  select: { padding: '7px 10px', border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: '0.85rem', background: '#fff' },
  tenantGroup: { marginBottom: 24 },
  tenantGroupHead: { display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 },
  tenantGroupName: { fontWeight: 800, color: INK, fontSize: '1rem' },
  tenantGroupSlug: { color: MUTED, fontSize: '0.78rem', fontFamily: 'monospace' },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 },
  wfCard: { textAlign: 'left', background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14, padding: 16, cursor: 'pointer', boxShadow: '0 1px 3px rgba(16,24,40,0.04)' },
  wfTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  wfIcon: { fontSize: '1.5rem' },
  wfName: { fontWeight: 700, color: INK, fontSize: '0.95rem' },
  wfCat: { color: MUTED, fontSize: '0.8rem', marginBottom: 10 },
  statusPill: { borderRadius: 999, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700, textTransform: 'capitalize' },
  progressTrack: { height: 8, background: '#eef2ff', borderRadius: 999, overflow: 'hidden' },
  progressTrackLg: { height: 12, background: '#eef2ff', borderRadius: 999, overflow: 'hidden', margin: '14px 0 20px' },
  progressFill: { height: '100%', background: ACCENT, borderRadius: 999 },
  wfMeta: { color: MUTED, fontSize: '0.75rem', marginTop: 6 },
  emptyCard: { background: '#fff', border: `1px dashed ${BORDER}`, borderRadius: 14, padding: 32, textAlign: 'center', color: MUTED },
  muted: { color: MUTED, padding: '20px 4px' },
  error: { background: '#fef2f2', border: '1px solid #fca5a5', color: '#7f1d1d', borderRadius: 8, padding: '10px 14px', marginBottom: 16, cursor: 'pointer' },
  // modal
  scrim: { position: 'fixed', inset: 0, background: 'rgba(11,17,32,0.45)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: '#fff', borderRadius: 14, padding: 22, width: 'min(440px, 92vw)', boxShadow: '0 20px 50px rgba(16,24,40,0.3)' },
  label: { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', margin: '12px 0 4px' },
  input: { width: '100%', boxSizing: 'border-box', padding: '9px 11px', border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: '0.9rem', background: '#fff' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 },
  // detail
  back: { background: 'none', border: 'none', color: ACCENT, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', padding: 0, marginBottom: 14 },
  detailHead: { display: 'flex', alignItems: 'center', gap: 14 },
  detailIcon: { width: 52, height: 52, borderRadius: 12, border: '2px solid', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem' },
  detailMeta: { color: MUTED, fontSize: '0.85rem', marginTop: 4 },
  statusBtn: { border: 'none', borderRadius: 999, padding: '6px 14px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', textTransform: 'capitalize' },
  deleteBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', padding: '4px 6px' },
  stepsHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  stepCard: { display: 'flex', gap: 12, background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, padding: 14, marginBottom: 10 },
  stepNum: { width: 26, height: 26, borderRadius: '50%', background: '#eef2ff', color: ACCENT, fontWeight: 700, fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  stepTitle: { width: '100%', boxSizing: 'border-box', border: 'none', fontSize: '0.95rem', fontWeight: 700, color: INK, padding: '2px 0', outline: 'none' },
  stepDesc: { width: '100%', boxSizing: 'border-box', border: `1px solid ${BORDER}`, borderRadius: 6, fontSize: '0.82rem', color: '#334155', padding: '6px 8px', marginTop: 4, resize: 'vertical', fontFamily: 'inherit' },
  stepRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 },
  assigneeSelect: { padding: '5px 8px', border: `1px solid ${BORDER}`, borderRadius: 6, fontSize: '0.8rem', background: '#fff' },
  assigneeInput: { flex: 1, padding: '5px 8px', border: `1px solid ${BORDER}`, borderRadius: 6, fontSize: '0.8rem' },
  assigneeCurrent: { fontSize: '0.72rem', color: MUTED, marginTop: 6 },
  stepStatusSelect: { padding: '5px 8px', border: `1px solid ${BORDER}`, borderRadius: 6, fontSize: '0.8rem', background: '#fff' },
  saveBtn: { background: ACCENT, color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' },
  stepDeleteBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', padding: '2px 4px' },
};
